import React from 'react';
import * as Styled from '../private/styled';
import { PoiId } from 'utils/entities';
import { IContactPageSubmitVariables } from 'store/sagas/apiService/types';
import { DeveloperContactProps } from 'components/contact/types';
import { ContactAgentModalSource, ContactModalOpenMeta } from 'store/state/app';
import { useScreenBreakpoint } from 'consts/breakpoints';
import ContactButtons from 'components/contact-buttons';
import { useLocale } from 'locale';
import { DeveloperLogo } from 'components/contact/DeveloperLogo';
import { Developers } from 'components/contact/Developers';
import { isLeadToMeetingEnabled } from 'consts/leadToMeeting';
import { useABTests } from 'hooks/useABTests';

interface POIDeveloperStickyProps extends DeveloperContactProps {
  id: PoiId;
  onSetContactModalPoiId: (id: PoiId, meta: ContactModalOpenMeta) => void;
  onOpenLeadToMeetingChooser?: (id: PoiId) => void;
  onCallClickEvent?: (isVisible: boolean, formValues: IContactPageSubmitVariables, source?: string) => void;
}

export const POIDeveloperSticky: React.FC<POIDeveloperStickyProps> = (props) => {
  const {
    onCallClickEvent,
    contactValues,
    onSetContactModalPoiId,
    onOpenLeadToMeetingChooser,
    phone,
    poiType,
    id,
    isContacted,
    developers,
    projectName,
    projectLogo,
    isPromoted,
  } = props;

  const { t } = useLocale();

  const screenBreakpoint = useScreenBreakpoint();
  const [ phoneNumberIsVisible, setPhoneNumberIsVisible ] = React.useState(false);

  const isMobile = screenBreakpoint < 3;
  const abTests = useABTests();

  const isChooserEnabled = poiType === 'project' && Boolean(onOpenLeadToMeetingChooser) && isLeadToMeetingEnabled(abTests, id);
  const mailButtonLabel = isChooserEnabled
    ? t('leadToMeeting.sticky.contactButton')
    : t('contactAgentStickyBar.contactAgentForm.open.alternative');

  const handleModalOpen = () => {
    if (isChooserEnabled) {
      onOpenLeadToMeetingChooser(id);
      return;
    }

    onSetContactModalPoiId(id, { source: ContactAgentModalSource.ContactSticky });
  };

  const handlePhoneSubmit = (e: React.MouseEvent<HTMLElement>) => {
    onCallClickEvent(isMobile || phoneNumberIsVisible, { ...contactValues, id, type: poiType }, ContactAgentModalSource.ContactSticky);

    if (!phoneNumberIsVisible && !isMobile) {
      e.preventDefault();
      setPhoneNumberIsVisible(true);
    }
  };

  if (isMobile) {
    return (
      <Styled.Root data-auto="developer-poi-sticky">
        <ContactButtons
          phone={isPromoted ? phone : undefined}
          onShowPhone={handlePhoneSubmit}
          isContacted={isContacted}
          handleModalOpen={handleModalOpen}
          showPhone={phoneNumberIsVisible}
          dataAttributePrefix="developer-contact-poi"
          phoneButtonLabel={isChooserEnabled ? t('leadToMeeting.sticky.callButton') : t('contactAgentModal.callButton')}
          mailButtonLabel={mailButtonLabel}
          equalWidthButtons
        />
      </Styled.Root>
    );
  }

  const formattedProjectName = t('projectPage.project.name', { name: projectName, isNotPromoted: !isPromoted });

  return (
    <Styled.Root data-auto="developer-poi-sticky" modeB>
      <Styled.InfoBox>
        <DeveloperLogo developerLogo={isPromoted ? projectLogo : undefined} size={56} />
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Styled.UserName weight="bold">{formattedProjectName}</Styled.UserName>
          {isPromoted ? <Developers developers={developers} /> : null}
        </div>
      </Styled.InfoBox>
      <ContactButtons
        phone={isPromoted ? phone : undefined}
        onShowPhone={handlePhoneSubmit}
        isContacted={isContacted}
        handleModalOpen={handleModalOpen}
        showPhone={phoneNumberIsVisible}
        dataAttributePrefix="developer-contact-poi"
        mailButtonLabel={mailButtonLabel}
      />
    </Styled.Root>
  );
};
